/** One extra native product batch, only in the homepage campaign section. */
class DjCampaignMore extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section');
    this.grid = this.section?.querySelector('[data-testid="product-list"] > .resource-list--grid');
    this.link = this.section?.querySelector('.section-resource-list__header a[href$="/collections/kampanjer"]');
    this.allLink = this.querySelector('[data-campaign-all]');
    this.status = this.querySelector('[data-campaign-status]');
    if (!this.grid || !this.link || !this.allLink || !this.status || !this.dataset.nextUrl || this.connected) return;
    this.connected = true;
    this.grid.id ||= `campaign-products-${this.dataset.sectionId}`;
    if (!this.expanded) {
      this.link.setAttribute('role', 'button');
      this.link.setAttribute('aria-controls', this.grid.id);
      this.link.setAttribute('aria-expanded', 'false');
    }
    this.clickHandler = (event) => this.onClick(event);
    this.keyHandler = (event) => {
      if (!this.expanded && event.key === ' ') {
        event.preventDefault();
        this.link.click();
      }
    };
    this.link.addEventListener('click', this.clickHandler);
    this.link.addEventListener('keydown', this.keyHandler);
  }

  disconnectedCallback() {
    this.request?.abort();
    clearTimeout(this.timer);
    this.link?.removeEventListener('click', this.clickHandler);
    this.link?.removeEventListener('keydown', this.keyHandler);
    this.connected = false;
  }

  uniqueCardIds(copy, productId) {
    // Static block IDs restart on each server page. Rename only real collisions.
    const renamed = new Map();
    for (const element of copy.querySelectorAll('[id]')) {
      if (!document.getElementById(element.id)) continue;
      const oldId = element.id;
      element.id = `${oldId}--campaign-more-${productId}`;
      renamed.set(oldId, element.id);
    }
    if (!renamed.size) return;
    for (const element of copy.querySelectorAll('[for], [form], [aria-labelledby], [aria-describedby], [aria-controls], [aria-owns], [href^="#"]')) {
      for (const attr of ['for', 'form', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns']) {
        const value = element.getAttribute(attr);
        if (value) element.setAttribute(attr, value.split(/\s+/).map((id) => renamed.get(id) || id).join(' '));
      }
      const href = element.getAttribute('href');
      if (href?.startsWith('#') && renamed.has(href.slice(1))) element.setAttribute('href', `#${renamed.get(href.slice(1))}`);
    }
  }

  async onClick(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button > 0) return;
    // A double click must not jump to the collection as the first request finishes.
    if (this.loading || event.detail > 1) {
      event.preventDefault();
      return;
    }
    if (this.expanded) return;
    event.preventDefault();
    this.loading = true;
    const label = this.link.textContent;
    this.link.textContent = 'Visar fler…';
    this.link.setAttribute('aria-disabled', 'true');
    this.grid.setAttribute('aria-busy', 'true');
    this.status.classList.add('visually-hidden');
    this.status.textContent = 'Hämtar fler kampanjprodukter.';
    this.request = new AbortController();
    this.timer = setTimeout(() => this.request.abort(), 12000);

    try {
      const url = new URL(this.dataset.nextUrl, window.location.href);
      if (url.origin !== window.location.origin) throw new Error('Invalid campaign origin');
      // Preserve locale and theme-preview context; only request this section.
      const current = new URL(window.location.href);
      for (const [key, value] of current.searchParams) {
        if (!url.searchParams.has(key)) url.searchParams.set(key, value);
      }
      url.searchParams.delete('sections');
      url.searchParams.set('section_id', this.dataset.sectionId);
      const response = await fetch(url, { signal: this.request.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Campaign request failed');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!this.isConnected) return;
      const returnedSection = page.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const returnedGrid = returnedSection?.querySelector('[data-testid="product-list"] > .resource-list--grid');
      if (!returnedGrid) throw new Error('Missing campaign grid');

      const seen = new Set(Array.from(this.grid.querySelectorAll('product-card[data-product-id]'), (card) => card.dataset.productId));
      const fragment = document.createDocumentFragment();
      const limit = Math.min(Number(this.dataset.batchSize) || 10, 20);
      let added = 0;
      let firstLink;
      for (const item of returnedGrid.children) {
        if (!item.classList.contains('resource-list__item') || added >= limit) continue;
        const id = item.querySelector('product-card')?.dataset.productId;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        const copy = document.importNode(item, true);
        this.uniqueCardIds(copy, id);
        // Native custom elements initialize on connection. Do not execute fetched scripts.
        copy.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])').forEach((script) => script.remove());
        firstLink ||= copy.querySelector('a.product-card__link');
        fragment.append(copy);
        added += 1;
      }
      if (!added) throw new Error('No additional campaign products');
      const focusNewProducts = document.activeElement === this.link;
      this.grid.append(fragment);
      this.expanded = true;
      this.dataset.expanded = 'true';
      this.link.textContent = label;
      this.link.setAttribute('aria-expanded', 'true');
      this.link.hidden = true;
      // This separate native link stays in normal flow after the complete grid.
      this.allLink.hidden = false;
      this.status.textContent = `${added} fler produkter visas. Knappen nedanför leder till alla kampanjer.`;
      // Keep keyboard users at the start of the new products, without a scroll jump.
      if (focusNewProducts) (firstLink || this.allLink).focus({ preventScroll: true });
    } catch (error) {
      if (!this.isConnected) return;
      this.link.textContent = label;
      this.status.classList.remove('visually-hidden');
      this.status.textContent = 'Det gick inte att hämta fler produkter just nu. Försök igen eller ';
      const fallback = document.createElement('a');
      fallback.href = this.link.href;
      fallback.textContent = 'se alla kampanjer';
      this.status.append(fallback, '.');
    } finally {
      clearTimeout(this.timer);
      this.loading = false;
      this.link.removeAttribute('aria-disabled');
      this.grid.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('dj-campaign-more')) {
  customElements.define('dj-campaign-more', DjCampaignMore);
}
